import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {IBaseComponent} from '../ibase/ibase.component';

export enum EStatusTagDisplayMode {
    ICON = 'ICON',
    TEXT = 'TEXT',
    BOTH = 'BOTH'
}

@Component({
    selector: 'app-status-tag',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './status-tag.component.html',
    styleUrl: './status-tag.component.scss'
})
export class StatusTagComponent extends IBaseComponent {
    protected readonly EStatusTagDisplayMode = EStatusTagDisplayMode;

    protected tagClass = '';
    protected iconClass = '';
    protected displayText = '';

    // ===============================================================
    // ===============================================================

    @Input() public status = '';
    @Input() public displayMode: EStatusTagDisplayMode = EStatusTagDisplayMode.ICON;
    @Input() public isLocked = false;

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this.tagClass = this.getCombinedClasses();
        this.iconClass = this.getIconClass();
        this.displayText = this.status;
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Get combined css classes for the tag
     * @returns Flat list of css classes
     */
    private getCombinedClasses(): string {
        const baseClass = this.displayMode === EStatusTagDisplayMode.ICON ? 'circle-tag' : 'text-tag';
        const colorClass = this.getColorClass();
        return `${baseClass} ${colorClass}`;
    }
    /**
     * Get color css classes
     * @returns Flat list of css classes
     */
    private getColorClass(): string {
        const upperStatus = this.status.toUpperCase();

        if (['ERROR', 'INVALID', 'ALERT'].includes(upperStatus)) {
            return 'color-red';
        } else if (['WARNING'].includes(upperStatus)) {
            return 'color-yellow';
        } else if (['VALID', 'COMPLETED', 'SUCCESS', 'DONE'].includes(upperStatus)) {
            return 'color-green';
        } else if (['RUNNING', 'WAITING', 'VERBOSE', 'INFO'].includes(upperStatus)) {
            return 'color-blue';
        } else {
            return 'color-default';
        }
    }
    /**
     * Get icon classes
     * @returns Flat list of css classes
     */
    private getIconClass(): string {
        const upperStatus = this.status.toUpperCase();

        if (['ERROR', 'INVALID'].includes(upperStatus)) {
            return 'pi-times';
        } else if (['VALID', 'COMPLETED', 'SUCCESS', 'DONE'].includes(upperStatus)) {
            return 'pi-check';
        } else if (['WAITING'].includes(upperStatus)) {
            return 'pi-question';
        } else if (['WARNING'].includes(upperStatus)) {
            return 'pi-exclamation-triangle';
        } else if (['VERBOSE'].includes(upperStatus)) {
            return 'pi-comment';
        } else if (['RUNNING', 'SCHEDULED'].includes(upperStatus)) {
            return 'pi-clock';
        } else if (['INFO'].includes(upperStatus)) {
            return 'pi-info-circle';
        } else if (['ALERT'].includes(upperStatus)) {
            return 'pi-exclamation-circle';
        } else {
            return 'pi-cog';
        }
    }
}
